import React, { useState, useEffect } from "react";
import { Tooltip } from "antd";
import "./InboxInvoiceList.css";
import { useDispatch, useSelector } from "react-redux";
import { setSelectedInvoice } from "../../../../Reducer/SelectedInvoiceReducer";
import CommonTable from "../../../../Components/CustomTable/CustomTable";
import { useInvoiceActions } from "../../../../hooks/useInvoiceActions";
import { UpdateInvoiceFlagService } from "../../../../Services/InvoiceService/InvoiceService";
import { getFileNameFromUrl } from "../../../../Utils/fileDownload";

// --- Types ---
interface RowData {
  id: number;
  stageId: number;
  isFlagged: boolean;
  user: string;
  date: string;
  supplier: string;
  category: string;
  discountAmount: number;
  documentOwner: string;
  total: number;
  selected: boolean;
  description?: string;
  taxAmount?: number;
  documentReference?: string;
}

interface InboxInvoiceListProps {
  setInvoiceView: React.Dispatch<React.SetStateAction<boolean>>;
  setStatementView: React.Dispatch<React.SetStateAction<boolean>>;
  invoicesDataSource: any;
  totalInvoiceCount: number;
  loadMoreInvoicesOnScroll: any;
  isLoadingMore: boolean;
  sortInvoices: any;
  selectedColumns: string[];
  // --- Lifted selection props ---
  selectedIds: number[];
  onRowSelect: (id: number, checked: boolean) => void;
  onSelectAll: (checked: boolean) => void;
}

// --- Constants ---
const ALWAYS_VISIBLE_COLUMNS = ["selected", "isFlagged", "ID", "Status"];

const InboxInvoiceList: React.FC<InboxInvoiceListProps> = ({
  setInvoiceView,
  setStatementView,
  invoicesDataSource,
  totalInvoiceCount,
  loadMoreInvoicesOnScroll,
  isLoadingMore,
  sortInvoices,
  selectedColumns,
  selectedIds,
  onRowSelect,
  onSelectAll,
}) => {
  // --- Redux ---
  const dispatch = useDispatch();
  const selectedClient = useSelector((state: any) => state.selectedClient);
  const applicationType = useSelector((state: any) => state.applicationType.applicationType);

  // --- Invoice Actions ---
  const { makeInvoiceViewed } = useInvoiceActions();

  // --- Flag State (local optimistic overrides only) ---
  const [flagOverrides, setFlagOverrides] = useState<Record<string | number, boolean>>({});

 const allRowIds = invoicesDataSource.map((row: RowData) => row.id);
const selectAll =
  allRowIds.length > 0 && allRowIds.every((id: number) => selectedIds.includes(id));




  const onRowCheckboxChange = (row: RowData, checked: boolean) => {
    //if (!checked) setSelectAll(false);
    onRowSelect(row.id, checked);
  };

  // Kept for CommonTable compatibility (clear-selection button inside table header)
  const handleClearSelection = () => {
   // setSelectAll(false);
    onSelectAll(false);
  };



  // --- Flag Click (optimistic update) ---
  const handleFlagClick = async (row: RowData) => {
    const currentFlagState = flagOverrides[row.id] ?? row.isFlagged;
    const newFlagState = !currentFlagState;

    // Optimistically update UI immediately
    setFlagOverrides((prev) => ({ ...prev, [row.id]: newFlagState }));

    const result = await UpdateInvoiceFlagService(Number(row.id), newFlagState);

    if (!result.success) {
      // Revert on failure
      setFlagOverrides((prev) => ({ ...prev, [row.id]: currentFlagState }));
    }
  };

  // --- Restore scroll position after data loads ---
  useEffect(() => {
    const savedScrollPosition = sessionStorage.getItem("tableScrollPosition");
    if (!savedScrollPosition) return;

    const interval = setInterval(() => {
      const tableWrapper = document.querySelector(".table-wrapper") as HTMLElement;
      if (!tableWrapper) return;

      tableWrapper.style.visibility = "hidden";
      tableWrapper.scrollTop = parseFloat(savedScrollPosition);

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          tableWrapper.style.visibility = "visible";
          sessionStorage.removeItem("tableScrollPosition");
          sessionStorage.removeItem("selectedInvoiceId");
        });
      });

      clearInterval(interval);
    }, 10);

    return () => clearInterval(interval);
  }, [invoicesDataSource]);

  // --- Columns ---
 // NEW
const handleInvoiceClick = async (row: RowData) => {
  const tableWrapper = document.querySelector('.table-wrapper') as HTMLElement;
  if (tableWrapper) {
    sessionStorage.setItem("tableScrollPosition", tableWrapper.scrollTop.toString());
  }
  sessionStorage.setItem("selectedInvoiceId", row.id.toString());
  dispatch(setSelectedInvoice(row));

  if (applicationType === "supplierStatements") {
    setStatementView(true);
  } else {
    setInvoiceView(true);
    makeInvoiceViewed(row.id, row.stageId);
  }
};
  const supplierStatementColumns = [
    {
      key: "selected",
      title: "selected",
      dataIndex: "",
      isCheckbox: true,
      showSortArrows: false,
    },
    {
      title: "Status",
      dataIndex: "stageId",
      key: "Status",
      isStatus: true,
      showSortArrows: true,
    },
    {
      title: "File",
      dataIndex: "statementFile",
      key: "statementFile",
      showSortArrows: false,
    },
    {
      title: "Supplier",
      dataIndex: "supplier",
      key: "supplier",
      showSortArrows: true,
    },
    {
      title: "Date",
      dataIndex: "invoiceDate",
      key: "invoiceDate",
      isDate: true,
      showSortArrows: true,
    },
    {
      title: "Outstanding Balance",
      dataIndex: "outstandingBalance",
      key: "statementBalance",
      showSortArrows: true,
    },
    {
      title: "User",
      dataIndex: "statementUser",
      key: "statementUser",
      showSortArrows: true,
    },
    {
      title: "Note",
      dataIndex: "statementNote",
      key: "statementNote",
      showSortArrows: false,
    },
  ];

  const columns = [
    {
      key: "selected",
      title: "selected",
      dataIndex: "",
      isCheckbox: true,
      showSortArrows: false,
    },
    {
      key: "isFlagged",
      title: "isFlagged",
      dataIndex: "isFlagged",
      showSortArrows: false,
      isFlag: true,
    },
    {
      title: "ID",
      dataIndex: "id",
      key: "invoiceid",
      showSortArrows: true,
      render: (text: string) => (
        <Tooltip title={text}>
          <span>{text?.length > 15 ? `${text.substring(0, 15)}...` : text}</span>
        </Tooltip>
      ),
    },
    {
      title: "Status",
      dataIndex: "stageId",
      key: "Status",
      isStatus: true,
      showSortArrows: true,
    },
    {
      title: "Submitted By",
      dataIndex: "submittedBy",
      key: "submittedBy",
      showSortArrows: true,
      render: (text: string | null, record: any) =>
        record.submissionMethod?.toLowerCase() === "email"
          ? ""
          : text || "",
    },
    {
      title: "Date",
      dataIndex: "invoiceDate",
      key: "invoiceDate",
      isDate: true,
      showSortArrows: true,
    },
    {
      title: "Supplier",
      dataIndex: "supplier",
      key: "supplier",
      showSortArrows: true,
      render: (text: string) => (
        <Tooltip title={text}>
          <span>{text?.length > 15 ? `${text.substring(0, 15)}...` : text}</span>
        </Tooltip>
      ),
    },
    {
      title: "Total",
      dataIndex: "totalAmount",
      key: "totalAmount",
      showSortArrows: true,
      render: (discountValue: number) => (
        <Tooltip title={discountValue}>
          <span>
            {discountValue != null
              ? discountValue.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })
              : "0.00"}
          </span>
        </Tooltip>
      ),
    },
    {
      title: "Tax",
      dataIndex: "taxAmount",
      key: "TAXAMOUNT",
      showSortArrows: true,
    },
    {
      title: "Document Ref",
      dataIndex: "documentReference",
      key: "DOCUMENTREFERENCE",
      showSortArrows: true,
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      showSortArrows: false,
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      showSortArrows: true,
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "Description",
      showSortArrows: true,
      render: (text: string) => (
        <Tooltip title={text}>
          <span>{text?.length > 15 ? `${text.substring(0, 15)}...` : text}</span>
        </Tooltip>
      ),
    },
    {
      title: "Due Date",
      dataIndex: "dueDate",
      key: "dueDate",
      showSortArrows: true,
      isDate: true,
      render: (text: string) => {
        const formattedDate = new Date(text).toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        });
        return (
          <Tooltip title={formattedDate}>
            <span>{formattedDate}</span>
          </Tooltip>
        );
      },
    },
    {
      title: "Paid",
      dataIndex: "paid",
      key: "Paid",
      showSortArrows: true,
    },
    {
      title: "Payment",
      dataIndex: "payment",
      key: "Payment",
      showSortArrows: true,
    },
    {
      title: "Submission Method",
      dataIndex: "Submission Method",
      key: "Submission Method",
      showSortArrows: true,
    },
    {
      title: "Upload Date",
      dataIndex: "createdDate",
      key: "createdDate",
      showSortArrows: true,
      isDate: true,
    },
  ];

  // --- Column Filtering ---
  const filteredColumns =
    applicationType === "supplierStatements"
      ? supplierStatementColumns
      : columns.filter(
          (col) =>
            ALWAYS_VISIBLE_COLUMNS.includes(col.title as string) ||
            selectedColumns.includes(col.title as string),
        );
  const tableData = invoicesDataSource.map((row: RowData & Record<string, any>) => {
    if (applicationType !== "supplierStatements") {
      return { ...row, isFlagged: flagOverrides[row.id] ?? row.isFlagged };
    }

    const balance = Number(
      row.outstandingBalance ?? row.totalAmount ?? row.amount ?? 0,
    );
    return {
      ...row,
      isFlagged: flagOverrides[row.id] ?? row.isFlagged,
      statementFile:
        row.fileName ??
        row.file ??
        (row.documentUrl ? getFileNameFromUrl(row.documentUrl) : "—"),
      statementBalance: Number.isFinite(balance)
        ? balance.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })
        : "—",
      statementUser:
        row.user || row.documentOwner || row.submittedBy || row.submittedByName || "—",
      statementNote: row.note || row.description || "—",
    };
  });

  return (
    <div className="InboxInvoiceList">
      <div className="custom-table-wrapper">
        <CommonTable
          // Merge flagOverrides into the data so the flag icon re-renders correctly
          data={tableData}
          sortInvoices={sortInvoices}
          onFlagClick={handleFlagClick}
          infinityScroll={true}
          loadMoreInvoicesOnScroll={loadMoreInvoicesOnScroll}
          columns={filteredColumns}
          selectedRowKeys={selectedIds}
          onRowCheckboxChange={onRowCheckboxChange}
          selectAll={selectAll}
          handleSelectAll={onSelectAll}
          handleClearSelection={handleClearSelection}
          onRow={(record: RowData) => ({
            onClick: (e) => {
              if ((e.target as HTMLElement).closest("input[type='checkbox']")) return;
              handleInvoiceClick(record);
            },
          })}
          stageId={25}
          userId={selectedClient.selectedClient?.userId}
          totalCount={totalInvoiceCount}
          // isDuplicate={true} // Pass the isDuplicate prop
        />
      </div>

      <h2 style={{ textAlign: "center", color: "var(--color-brand)" }}>
        {isLoadingMore ? " Loading... " : invoicesDataSource.length} /{" "}
        {totalInvoiceCount}
      </h2>
    </div>
  );
};

export default InboxInvoiceList;
